import { UserCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

interface UserMenuProps {
  variant?: "mobile" | "desktop";
}

export const UserMenu = ({ variant = "desktop" }: UserMenuProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSignOut = async () => {
    try {
      // Clear any persisted storage first
      localStorage.clear();
      sessionStorage.clear();

      // Navigate away immediately
      navigate("/");
      
      // Show success message
      toast({
        description: "You have been signed out.",
      });

      // Attempt cleanup in the background
      try {
        // Try local session cleanup first
        await supabase.auth.signOut({ scope: 'local' });
        
        // Then try global cleanup if local succeeds
        await supabase.auth.signOut({ scope: 'global' });
      } catch (cleanupError: any) {
        // Log cleanup errors but don't show to user since they're already signed out
        console.error("Session cleanup error:", cleanupError);
      }
    } catch (err) {
      console.error("Sign out error:", err);
      // Force navigation and show error
      navigate("/");
      toast({
        variant: "destructive",
        description: "Session cleared. Please refresh the page if needed.",
      });
    }
  };

  if (variant === "mobile") {
    return (
      <>
        <div className="flex items-center space-x-2 text-neutral px-4">
          <UserCheck size={24} />
        </div>
        <div
          onClick={() => navigate("/dashboard")}
          className="text-neutral hover:text-primary transition px-4 cursor-pointer"
        >
          Dashboard
        </div>
        <button
          onClick={handleSignOut}
          className="text-neutral hover:text-primary transition px-4 text-left"
        >
          Sign Out
        </button>
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center text-neutral hover:text-primary transition">
        <UserCheck size={24} />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => navigate("/dashboard")}>
          Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSignOut}>
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};